import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ListProformaParties,
  GetProformaInvoiceNextNumber,
  AddProformaInvoice,
  GetProformaState,
  GetProformaSettings,
  GetProformaCustomerDetail,
} from "../../store/slices/proforma-invoice";
import { toast } from "react-hot-toast";
import InvoiceSecond from "./AddProformaInvoiceSecond";
import Select from "react-select";
import Loader from "../../common/Loader";
import { useDispatch} from "react-redux";
import AdminLayout from "../AdminLayout";
import AddLedgerPop from "../AddLedgerPop";

const AddInvoice = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = JSON.parse(localStorage.getItem("user"));
  const id = user?.data?.profile_id; // profile_id
  const savedFinancialYear = localStorage.getItem("selectedFinancialYear");

  const getFinancialYearRange = (financialYear) => {
    if (financialYear && financialYear.includes("-")) {
      const [startYear, endYear] = financialYear.split("-");
      return {
        minDate: `${startYear}-04-01`,
        maxDate: `${endYear}-03-31`,
      };
    }

    const currentYear = new Date().getFullYear();
    return {
      minDate: `${currentYear - 1}-04-01`,
      maxDate: `${currentYear}-03-31`,
    };
  };

  const clampDateToRange = (date, minDate, maxDate) => {
    if (date < minDate) return minDate;
    if (date > maxDate) return maxDate;
    return date;
  };

  const currentDate = new Date().toISOString().split("T")[0];
  const { minDate, maxDate } = getFinancialYearRange(savedFinancialYear);
  const selectedStartYear = savedFinancialYear?.includes("-")
    ? savedFinancialYear.split("-")[0]
    : String(new Date().getFullYear() - 1);
  const initialInvoiceDate = clampDateToRange(`${selectedStartYear}-${currentDate.slice(5)}`, minDate, maxDate);

  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [listParties, setListParties] = useState([]);
  const [state, setState] = useState([]);
  const [getInvoicesNumber, setGetInvoicesNumber] = useState();
  const [isSameAsBilling, setIsSameAsBilling] = useState(false);
  const [invoiceSecond, setInvoiceSecond] = useState({});
  const [invoicePrefix, setInvoicePrefix] = useState();
  const [selectedPartyDetails, setSelectedPartyDetails] = useState({
    address: "",
    gstin: "",
    phone: "",
    state: "",
    ledger_id: "",
    city: "",
    pin_code: "",
  });
  const [shippingAddress, setShippingAddress] = useState({
    address: "",
    gstin: "",
    phone: "",
    state: "",
    city: "",
    pin_code: "",
  });

  // New state for additional fields
  const [formData, setFormData] = useState({
    invoice_date: initialInvoiceDate,
    eway_number: "",
    vehicle_number: "",
    message: "",
    invoice_number: getInvoicesNumber,
    po_number: "",
  });

  const [errors, setErrors] = useState({});

  const fetchInvoiceNumber = async () => {
    try {
      const data = await dispatch(
        GetProformaInvoiceNextNumber({
          profile_id: id,
          financialYear: savedFinancialYear,
        })
      ).unwrap();
      setGetInvoicesNumber(data?.next_invoice_number);
      setFormData((prevData) => ({
        ...prevData,
        invoice_number: data?.next_invoice_number,
      }));
    } catch (error) {
      console.log("Error fetching invoice number:", error.message);
    }
  };

  // Function to get list of parties
  const fetchParties = async () => {
    try {
      const data = await dispatch(
        ListProformaParties({
          profile_id: id,
        })
      ).unwrap();
      setListParties(data?.data);
    } catch (error) {
      console.log("Error fetching parties:", error.message);
    }
  };

  // Function to get settings
  const fetchSettings = async () => {
    try {
      const data = await dispatch(GetProformaSettings()).unwrap();
      setInvoicePrefix(data?.data?.invoice_prefix);
    } catch (error) {
      console.log("Error fetching settings:", error.message);
    }
  };

  const fetchState = async () => {
    try {
      const data = await dispatch(GetProformaState()).unwrap();
      setState(data?.data);
    } catch (error) {
      console.log("Error fetching State:", error.message);
    }
  };

  // useEffect to call all the three functions
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([fetchInvoiceNumber(), fetchParties(), fetchSettings(), fetchState()]);
      setIsLoading(false);
    };

    fetchData();
  }, [dispatch, id]);

  // Party selection logic
  const partyOptions = listParties.map((party) => ({
    value: party.id,
    label: party.ledger,
  }));

  const handlePartyChange = async (selectedOption) => {
    if (!selectedOption) return;

    const fallbackParty = listParties.find((p) => p.id === selectedOption.value);
    let party = fallbackParty;

    try {
      const data = await dispatch(
        GetProformaCustomerDetail({ profile_id: id, customer_id: selectedOption.value })
      ).unwrap();
      party = data?.data || fallbackParty;
    } catch (error) {
      console.log("Error fetching customer detail:", error.message);
    }

    if (party) {
      setSelectedPartyDetails({
        address: party.address,
        gstin: party.gstn,
        phone: party.phone_number || party.phone,
        state: party.state,
        ledger_id: party.id || selectedOption.value,
        city: party.city,
        pin_code: party.pin_code,
      });

      if (isSameAsBilling) {
        setShippingAddress({
          address: party.address,
          gstin: party.gstn,
          phone: party.phone_number || party.phone,
          state: party.state,
          city: party.city,
          pin_code: party.pin_code,
        });
      }
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle input changes for selectedPartyDetails (Billing Address)
  const handleBillingInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedPartyDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  // Shipping address changes
  const handleShippingInputChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  // Checkbox for "Same as Billing Address"
  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setIsSameAsBilling(checked);
    if (checked) {
      setShippingAddress({
        address: selectedPartyDetails.address,
        gstin: selectedPartyDetails.gstin,
        phone: selectedPartyDetails.phone,
        state: selectedPartyDetails.state,
        city: selectedPartyDetails.city,
        pin_code: selectedPartyDetails.pin_code,
      });
    }
  };

  const validateForm = () => {
    let newErrors = {};
    if (!selectedPartyDetails.ledger_id) {
      newErrors.customer = "Customer is required.";
    }
    if (!selectedPartyDetails.state) {
      newErrors.selectedPartyState = "Billing state is required.";
    }
    // if (!shippingAddress.state ) {
    //   newErrors.shippingState = 'Shipping state is required.';
    // }
    if (!formData.invoice_number) {
      newErrors.invoice_number = "Invoice Number is required.";
    }
    if (invoiceSecond.performa_invoice_items?.length == 0) {
      toast.error("Invoice items is required.");
      return;
    }

    setErrors(newErrors);
    // If there are no errors, return true. Otherwise, return false.
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const billingData = {
      profile_id: Number(id),
      invoice_prefix: invoicePrefix,
      ledger_id: Number(selectedPartyDetails?.ledger_id), // Assuming this is hardcoded for now
      invoice_number: formData?.invoice_number,
      invoice_date: formData?.invoice_date,
      fin_year: "2024-2025",
      po_number: formData?.po_number,
      billing_address: selectedPartyDetails?.address,
      billing_city: selectedPartyDetails?.city,
      billing_pin_code: selectedPartyDetails?.pin_code,
      billing_state: selectedPartyDetails?.state,
      billing_phone: selectedPartyDetails?.phone || null,
      party_gstn: selectedPartyDetails?.gstin || null,
      shipping_address: shippingAddress?.address,
      shipping_city: shippingAddress?.city,
      shipping_pin_code: shippingAddress?.pin_code,
      shipping_state: shippingAddress?.state,
      shipping_phone: shippingAddress?.phone || null,
      eway_bill: formData?.eway_number,
      vehicle_number: formData?.vehicle_number,
      notes: formData?.message,
    };
      // console.log("Data to be sent:", billingData);

    const mergedData = {
      ...billingData,
      ...invoiceSecond,
    };

    console.log("Data to be sent:", mergedData);

    setIsLoading(true);

    try {
      // Use await to wait for the dispatch to finish
      await dispatch(AddProformaInvoice(mergedData)).unwrap();
      toast.success("Add Invoice Successfully");
      setIsLoading(false);
      navigate("/proforma-invoice-list");
    } catch (error) {
      setIsLoading(false);
      console.log(error.message);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false); // Close the modal
  };

  const handleOpenModal = () => {
    setIsModalOpen(true); // Open the modal
  };

  const handleLedgerAdded = async () => {
    await fetchParties();
  };

  return (
    <AdminLayout>
      {isLoading && <Loader />}
      {isModalOpen && <AddLedgerPop show={isModalOpen} onClose={handleCloseModal} onLedgerAdded={handleLedgerAdded} />}
      <div className="row content-body">
        <div className="container">
          <div className="page-header">
            <div>
              <h2 className="main-content-title tx-24 mg-b-5">Invoice</h2>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="#" onClick={() => navigate("/proforma-invoice-list")}>
                    Proforma Invoices
                  </a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Add Proforma Invoice
                </li>
              </ol>
            </div>
            <div className="d-flex justify-content-end">
              <button className="btn btn-cancel" onClick={() => navigate("/proforma-invoice-list")}>
                Cancel
              </button>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="card custom-card">
                <div className="card-body">
                  <div className="form-group">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="row">
                          <div className="col-md-12">
                            <label className="d-flex justify-content-between">
                              <span>
                                Customer <span className="required">*</span>
                              </span>
                              <button className="btn btn-default" onClick={handleOpenModal}>
                                Add Ledger
                              </button>
                            </label>
                            <Select options={partyOptions} placeholder="--Select Customer--" onChange={handlePartyChange} />
                            {errors.customer && <p className="text-danger">{errors.customer}</p>}
                          </div>
                        </div>
                        <fieldset className="form-group border p-2 mt-3">
                          <legend className="px-2">Billing Address</legend>
                          <div className="row mt-2">
                            <div className="col-md-6">
                              <label>Address </label>
                              <input name="address" type="text" className="form-control" value={selectedPartyDetails?.address} onChange={handleBillingInputChange} />
                            </div>
                            <div className="col-md-6">
                              <label>City </label>
                              <input name="city" type="text" className="form-control" value={selectedPartyDetails?.city} onChange={handleBillingInputChange} />
                            </div>
                           
                            <div className="col-md-6">
                              <label>
                                State <span className="required">*</span>
                              </label>
                              {/* <input name="state" type="text" className="form-control" value={selectedPartyDetails?.state} onChange={handleBillingInputChange} /> */}
                              <select className="form-control" name="state" value={selectedPartyDetails?.state || ""} onChange={handleBillingInputChange}>
                                <option value="">--Select State--</option>
                                {(state || []).map((option, index) => (
                                  <option key={index} value={option?.state_name}>
                                    {option?.state_name}
                                  </option>
                                ))}
                              </select>
                              {errors.selectedPartyState && <p className="text-danger">{errors.selectedPartyState}</p>}
                            </div>
                             <div className="col-md-6">
                              <label>Pin Code </label>
                              <input name="pin_code" type="text" className="form-control" value={selectedPartyDetails?.pin_code} onChange={handleBillingInputChange} />
                            </div>
                          </div>
                          

                          <div className="row mt-2">
                            <div className="col-md-6">
                              <label>GSTN </label>
                              <input name="gstin" type="text" className="form-control" value={selectedPartyDetails?.gstin} onChange={handleBillingInputChange} />
                            </div>
                            <div className="col-md-6">
                              <label>Phone </label>
                              <input name="phone" type="text" className="form-control" value={selectedPartyDetails?.phone} onChange={handleBillingInputChange} />
                            </div>
                          </div>
                        </fieldset>
                      </div>

                      <div className="col-md-6">
                        <div className="row">
                          <div className="col-md-12 mt-4">
                            <label className="ckbox pt-3">
                              <input type="checkbox" className="mx-2" checked={isSameAsBilling} onChange={handleCheckboxChange} />
                              <span>Same as Billing Address</span>
                            </label>
                          </div>
                        </div>

                        <fieldset className="form-group border p-2 mt-4">
                          <legend className="px-2">Shipping Address</legend>
                          <div className="row mt-2">
                            <div className="col-md-6">
                              <label>Address </label>
                              <input name="address" type="text" className="form-control" value={shippingAddress.address} onChange={handleShippingInputChange} />
                            </div>
                            <div className="col-md-6">
                              <label>City</label>
                              <input name="city" type="text" className="form-control" value={shippingAddress.city} onChange={handleShippingInputChange} />
                            </div>
                          
                            <div className="col-md-6">
                              <label>State</label>
                              {/* <input name="state" type="text" className="form-control" value={shippingAddress.state} onChange={handleShippingInputChange} required /> */}
                              <select className="form-control" name="state" value={shippingAddress.state || ""} onChange={handleShippingInputChange}>
                                <option value="">--Select State--</option>
                                {(state || []).map((option, index) => (
                                  <option key={index} value={option?.state_name}>
                                    {option?.state_name}
                                  </option>
                                ))}
                              </select>
                              {errors.shippingState && <p className="text-danger">{errors.shippingState}</p>}
                            </div>
                            <div className="col-md-6">
                              <label>Pin Code</label>
                              <input name="pin_code" type="text" className="form-control" value={shippingAddress.pin_code} onChange={handleShippingInputChange} />
                            </div>
                          </div>
                            

                          <div className="row mt-2">
                            {/* <div className="col-md-6">
                                  <label>GSTN </label>
                                  <input name="gstin" type="text" className="form-control" value={shippingAddress.gstin } onChange={handleShippingInputChange} />
                                </div> */}
                            <div className="col-md-6">
                              <label>Phone </label>
                              <input name="phone" type="text" className="form-control" value={shippingAddress.phone} onChange={handleShippingInputChange} />
                            </div>
                          </div>
                        </fieldset>
                      </div>
                    </div>
                    <hr />
                    <div className="row">
                      <div className="col-md-6">
                        <div className="row">
                          <div className="col-md-6">
                            <label>
                              Invoice Number <span className="required">*</span>
                            </label>
                            <div className="d-flex align-items-center">
                              <span
                                className="me-2 "
                                style={{
                                  paddingRight: "1rem",
                                }}
                              >
                                {invoicePrefix}
                              </span>
                              <input
                                name="invoice_number"
                                type="text"
                                className="form-control"
                                value={formData.invoice_number}
                                onChange={handleInputChange}
                                style={{
                                  width: "90%",
                                }} // This ensures the input takes the remaining space
                              />
                            </div>
                            {errors.invoice_number && <p className="text-danger">{errors.invoice_number}</p>}
                          </div>

                          <div className="col-md-6">
                            <label>Invoice Date </label>
                            <input
                              name="invoice_date"
                              type="date"
                              className="form-control"
                              value={formData?.invoice_date || initialInvoiceDate}
                              min={minDate}
                              max={maxDate}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>

                        <div className="row mt-3">
                          <div className="col-md-6">
                            <label>E-Way Bill Number </label>
                            <input name="eway_number" type="text" className="form-control" value={formData.eway_number} onChange={handleInputChange} />
                          </div>

                          <div className="col-md-6">
                            <label>Vehicle Number </label>
                            <input name="vehicle_number" type="text" className="form-control" value={formData.vehicle_number} onChange={handleInputChange} />
                          </div>
                        </div>
                        <div className="row mt-3">
                          <div className="col-md-6">
                            <label>PO Number </label>
                            <input name="po_number" type="text" className="form-control" value={formData.po_number} onChange={handleInputChange} />
                          </div>

                          <div className="col-md-6"></div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <label>Notes </label>
                        <textarea name="message" className="form-control" rows="6" cols="70" value={formData.message} onChange={handleInputChange}></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <InvoiceSecond onChildDataChange={setInvoiceSecond} onSubmit={handleSubmit} />
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AddInvoice;
